import { useEffect, useRef, useState } from 'react';
import { Icon, IconID } from '@common/Icon/Icon';
import { Button, TYPE } from '@common/Button/Button';
import Cookies from 'js-cookie';
import { useLocaleSelectionContext } from '@hooks/useLocaleSelectionContext';
import { TRANSLATION_CONFIGURATION, getLocale, localesThatUseIPForPrices } from '@libs/constants';
import { useRouter } from 'next/router';
import { Locale } from '@formatjs/intl-locale';
import { useSessionStore } from '@hooks/states/useSessionStore';
import { getLocaleItemForLocaleString } from '@libs/i18n.helper';
import type { ProductFragmentEnhanced } from '@models/enhanced-fragments';
import type { LanguageAlternate } from '@models/data';
import { useLocaleSelection } from '@hooks/useLocaleSelection';

interface DeliveryLocationBannerProps {
  title: string;
  text: string;
  allLocalesLabel: string;
  gotoLabel: string;
  currentProduct?: ProductFragmentEnhanced;
  languageAlternates: ReadonlyArray<LanguageAlternate>;
}

export const DeliveryLocationBanner = ({
  title,
  text,
  allLocalesLabel,
  gotoLabel,
  currentProduct,
  languageAlternates,
}: DeliveryLocationBannerProps) => {
  const router = useRouter();

  const [deactivatedByUser, setDeactivatedByUser] = useState(true);
  const [visible, setVisible] = useState(false);

  const gotoUrl = useRef('');
  const gotoLabelRef = useRef('');

  const { toggleLocaleSelectionVisibility } = useLocaleSelectionContext();

  const session = useSessionStore(state => state.session);

  const { getCurrentUrlForTargetLocale } = useLocaleSelection({ languageAlternates, currentProduct });

  useEffect(() => {
    // we only have to check the existence of the cookie here. Its value is not important.
    const deactivatedByCookie = !!Cookies.get('PriceDomainInfo');
    let countriesMismatch = false;
    let isIPDependentPriceCountry = false;

    if (session) {
      // TODO remove quickfix, but we have to find a robust general solution for the UK problem (= UK is often used wrongly as country-code for Great Britain which is in fact GB e.g. in the session)
      const sessionUserCountry = session.userCountry === 'uk' ? 'GB' : session.userCountry.toUpperCase();

      const targetLocale =
        getLocaleItemForLocaleString(`${session.language}-${sessionUserCountry}`) ||
        getLocaleItemForLocaleString(TRANSLATION_CONFIGURATION.fallbackLocale);

      // we do not want to redirect only to the target domain of the locale, but also keep the current url path and do necessary transformations (e.g. to the product id, if present), to keep the full current context
      const targetUrl = new URL(getCurrentUrlForTargetLocale(targetLocale));
      gotoUrl.current = targetUrl.href;

      // value retrieved from domain config might not contain protocol, but it is needed to be set as new window.location.href
      // Therefore we get the current protocol being used and attach it to the just created URL object
      targetUrl.protocol = window.location.protocol;

      gotoLabelRef.current = gotoLabel?.replace('{url}', targetUrl.hostname) || '';

      const locale = getLocale(router);
      const { region } = new Locale(locale);

      // we assume we don't want to show the banner when no region is defined in the router, like it is the case for the .eu domain
      countriesMismatch = region ? sessionUserCountry !== region : false;

      isIPDependentPriceCountry = !localesThatUseIPForPrices.includes(locale);
    }

    setVisible(!deactivatedByCookie && countriesMismatch && isIPDependentPriceCountry);
  }, [gotoLabelRef, gotoLabel, router, session]);

  const handleCloseClick = () => {
    // cookie name and value are inherited from the 'old' shop to ensure compatibility
    Cookies.set('PriceDomainInfo', '1', { path: '/' });
    setDeactivatedByUser(false);
  };

  const handleGotoClick = () => {
    if (gotoUrl) {
      window.location.href = gotoUrl.current;
    }
  };

  return visible && deactivatedByUser ? (
    <div
      data-testid="delivery-location-redirect-banner"
      className="bg-gray-500 text-2xl text-base text-white xl:h-delivery-location-banner mb-5.5 w-full py-5"
    >
      <div className="container md:flex justify-between py-1">
        <div>
          <h3 className="pb-3">{title}</h3>
          <p className="text-xs max-w-6xl">{text}</p>
        </div>
        <div className="justify-end pt-0.5 lg:flex-initial sm:pt-5">
          <span
            onClick={toggleLocaleSelectionVisibility}
            className="cursor-pointer text-sm pt-7 lg:pr-5 md:pr-0 md:pl-5 sm:pl-0 sm:pr-5"
          >
            {allLocalesLabel}
          </span>
          <Button
            onClick={handleGotoClick}
            type={TYPE.PRIMARY}
            rounded
            minW={false}
            className="px-5"
            text={gotoLabelRef.current ?? ''}
            testId="delivery-location-redirect-button"
          />
        </div>
      </div>
      <div onClick={handleCloseClick} className="cursor-pointer absolute top-2 right-8" data-testid="banner-close">
        <Icon id={IconID.CLOSE_CROSS} size={5} primary={false} />
      </div>
    </div>
  ) : null;
};
